import { useState } from 'react';
import { Alert, Box, Container, MenuItem, Typography, Stack, TextField } from '@mui/material';
import { getSignupAttribution, track } from '../../lib/analytics';
import { joinMailingList } from '../../lib/api';
import { SOUTHEAST_ASIA_COUNTRY_OPTIONS } from '../../lib/countries';
import Button from '../../components/Button';

function Hero() {
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  async function subscribe(event) {
    event.preventDefault();
    setStatus('submitting');
    setError('');
    try {
      await joinMailingList(email, country, getSignupAttribution());
      track('mailing_list_joined', { path: window.location.pathname, label: 'ugc_opportunities' });
      setStatus('success');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  }

  return (
    <Box id="ugc-opportunities" sx={{ bgcolor: '#F7F5FC', borderBottom: 1, borderColor: 'divider', scrollMarginTop: 72 }}>
      <Container maxWidth="md" sx={{ py: { xs: 8, sm: 10, md: 13 }, textAlign: 'center' }}>
        <Typography
          component="p"
          variant="overline"
          fontWeight={800}
          sx={{ display: 'inline-block', mb: 1, color: 'primary.main', letterSpacing: '0.12em' }}
        >
          Made for Southeast Asian creators
        </Typography>
        <Box
          aria-hidden="true"
          sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: { xs: 0.65, sm: 0.9 }, mb: 2.5, fontSize: { xs: '1.25rem', sm: '1.4rem' }, lineHeight: 1 }}
        >
          {SOUTHEAST_ASIA_COUNTRY_OPTIONS.map((countryOption) => (
            <Box component="span" key={countryOption.name} title={countryOption.name}>
              {countryOption.flag}
            </Box>
          ))}
        </Box>
        <Typography
          component="h1"
          variant="h2"
          fontWeight={800}
          sx={{ fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.35rem' }, lineHeight: 1.05, letterSpacing: '-0.035em', mb: 3 }}
        >
          UGC opportunities for Southeast Asian creators.
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ mx: 'auto', mb: 4, maxWidth: 650, fontSize: { xs: '1.05rem', sm: '1.2rem' }, lineHeight: 1.65 }}
        >
          Join our opportunity list and we’ll email you when relevant UGC briefs become available across the region.
        </Typography>
        <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'left' }}>
          <Box component="form" onSubmit={subscribe}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
              <TextField
                id="ugc-opportunity-email"
                label="Email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
                error={Boolean(error)}
                sx={{ flex: 1.15, bgcolor: 'background.paper' }}
              />
              <TextField
                id="ugc-opportunity-country"
                label="Country you’re based in"
                select
                value={country}
                onChange={(event) => setCountry(event.target.value)}
                required
                sx={{ flex: 0.85, minWidth: { md: 220 }, bgcolor: 'background.paper' }}
              >
                {SOUTHEAST_ASIA_COUNTRY_OPTIONS.map((countryOption) => (
                  <MenuItem key={countryOption.name} value={countryOption.name}>
                    <Box component="span" aria-hidden="true" sx={{ mr: 1.25, fontSize: '1.15rem', lineHeight: 1 }}>
                      {countryOption.flag}
                    </Box>
                    {countryOption.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button type="submit" disabled={status === 'submitting'} sx={{ whiteSpace: 'nowrap', px: 3, minHeight: 56 }}>
                {status === 'submitting' ? 'Joining…' : 'Send me UGC opportunities'}
              </Button>
            </Stack>
            {status === 'success' && (
              <Alert severity="success" variant="outlined" sx={{ mt: 1.5, bgcolor: 'background.paper' }}>
                You’re on the opportunity list. We’ll email you when new UGC briefs are available.
              </Alert>
            )}
            {error && (
              <Typography role="alert" variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                {error}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, textAlign: 'center' }}>
              Free to join. Opportunity availability varies.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Hero;
